'use client';

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { reloadBlockerRegistry } from '@/lib/reload-blockers';
import {
  folderUploadManager,
  type FolderUploadManager,
} from '@/lib/uploads/folder-upload/manager';
import { hasActiveFolderUploads } from '@/lib/uploads/folder-upload/summary';
import type { FolderUploadsSnapshot } from '@/lib/uploads/folder-upload/types';

const EMPTY: FolderUploadsSnapshot = { jobs: [] };
const Context = createContext<FolderUploadsSnapshot>(EMPTY);

/** Leaving the page mid-upload loses the rest: ask the browser to confirm. */
function useWarnOnUnloadWhileUploading(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy browsers only show the prompt when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [active]);
}

/** A deploy-skew reload waits until every folder upload has stopped. */
function useFolderUploadReloadSafety(manager: FolderUploadManager) {
  const instance = useId();
  useLayoutEffect(() => {
    const read = () =>
      hasActiveFolderUploads(manager.store.getSnapshot().jobs)
        ? 'blocked'
        : 'safe';
    const owner = reloadBlockerRegistry.registerReloadBlocker(
      `folder-uploads:${instance}`,
      read
    );
    const unsubscribe = manager.store.subscribe(() => owner.set(read));
    return () => {
      unsubscribe();
      owner();
    };
  }, [manager, instance]);
}

/**
 * App-wide folder uploads (#5823). The jobs live in a store outside React,
 * so they keep running across in-app navigation; this provider exposes them
 * to the panel and guards tab close and reloads while one is running.
 */
export function FolderUploadsProvider({
  children,
  manager = folderUploadManager,
}: {
  children: ReactNode;
  manager?: FolderUploadManager;
}) {
  const snapshot = useSyncExternalStore(
    manager.store.subscribe,
    manager.store.getSnapshot,
    () => EMPTY
  );
  useWarnOnUnloadWhileUploading(hasActiveFolderUploads(snapshot.jobs));
  useFolderUploadReloadSafety(manager);
  return <Context.Provider value={snapshot}>{children}</Context.Provider>;
}

export function useFolderUploadJobs() {
  return useContext(Context).jobs;
}
